import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const features = [
  {
    icon: 'Star',
    title: 'Vedic Astrology for Real Decisions',
    description: 'Jyotish maps your Dasha periods, planetary transits, and birth chart to reveal the optimal timing for business launches, investments, and life transitions. Not predictions. Probabilities you can act on.',
  },
  {
    icon: 'Home',
    title: 'Vastu Shastra for Wealth Flow',
    description: 'Vastu is the ancient science of directional energy in built spaces. A misaligned office silently suppresses cash flow, team performance, and decision clarity. We correct this without tearing down walls.',
  },
  {
    icon: 'Briefcase',
    title: 'Astrologically-Guided Strategy',
    description: 'Tailored for founders, executives, and NRIs managing complex portfolios. We combine Dashamsha, Ashtakavarga scores, and Muhurta science to give you a strategic edge no conventional consultant can replicate.',
  },
];

export default function ValueProp() {
  return (
    <section className="py-28 px-6 md:px-12 bg-white relative border-y border-stone-200">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
          
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
          >
            <p className="text-brand-gold text-sm uppercase tracking-[0.3em] font-bold mb-5">
              The Science Behind the Practice
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-dark mb-8 leading-tight">
              Vedic Astrology & Vastu. <br />
              <span className="italic text-brand-gold">Demystified.</span>
            </h2>
            <p className="text-stone-600 text-xl mb-14 leading-relaxed font-medium">
              For 5,000 years, Indian royalty and merchant families used Jyotish and Vastu as their strategic counsel. Today, Acharyaji brings the same mathematical precision to global business owners — stripped of mysticism, applied with clarity.
            </p>
            
            <ul className="space-y-12">
              {features.map((feature, idx) => (
                <motion.li 
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15, duration: 0.7 }}
                  className="flex gap-6"
                >
                  <div className="flex-shrink-0 mt-1 w-14 h-14 rounded-full bg-brand-accent border border-brand-gold/30 flex items-center justify-center text-brand-dark shadow-sm">
                    <SafeIcon name={feature.icon} className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif text-brand-dark mb-3">{feature.title}</h3>
                    <p className="text-stone-600 font-medium text-lg leading-relaxed">{feature.description}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Geometric diagram */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="relative h-[600px] rounded-sm overflow-hidden bg-brand-dark flex items-center justify-center shadow-2xl"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/10 via-transparent to-transparent pointer-events-none"></div>
            
            <div className="relative w-full max-w-md aspect-square flex items-center justify-center">
              {[100, 75, 50].map((pct, i) => (
                <div 
                  key={i}
                  className="absolute rounded-full border border-brand-gold/30"
                  style={{ 
                    width: `${pct}%`, height: `${pct}%`,
                    animation: `slow-spin ${20 + i * 8}s linear infinite`,
                    animationDirection: i % 2 === 0 ? 'normal' : 'reverse',
                  }}
                />
              ))}
              
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 200 200">
                <polygon points="100,10 190,175 10,175" fill="none" stroke="#D4AF37" strokeWidth="0.5" />
                <polygon points="100,190 10,25 190,25" fill="none" stroke="#D4AF37" strokeWidth="0.5" />
              </svg>

              <div className="w-24 h-24 bg-brand-gold/20 border-2 border-brand-gold/50 rounded-sm rotate-45 flex items-center justify-center z-10 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-full bg-brand-gold/30 -rotate-45 flex items-center justify-center">
                  <SafeIcon name="Star" className="text-brand-gold text-2xl" />
                </div>
              </div>

              {[
                { label: 'Jyotish', pos: 'top-6 left-1/2 -translate-x-1/2' },
                { label: 'Vastu', pos: 'bottom-6 left-1/2 -translate-x-1/2' },
                { label: 'Muhurta', pos: 'left-6 top-1/2 -translate-y-1/2' },
                { label: 'Dasha', pos: 'right-6 top-1/2 -translate-y-1/2' },
              ].map((l, i) => (
                <span key={i} className={`absolute text-brand-gold text-xs uppercase tracking-widest font-bold ${l.pos}`}>
                  {l.label}
                </span>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}